import { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8 text-gray-800">
      <article className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow md:p-10">
        <h1 className="mb-8 border-b border-gray-300 pb-5 text-left text-3xl font-bold text-gray-950">{title}</h1>
        <div className="space-y-7 leading-8">{children}</div>
        <div className="mt-10 border-t pt-6 text-left"><Link to="/" className="text-pink-700 underline">{t('backToHome')}</Link></div>
      </article>
    </div>
  );
}

const Bullets = ({ children }: { children: string[] }) => <ul className="list-disc space-y-2 pl-6">{children.map(text => <li key={text}>{text}</li>)}</ul>;
const Numbers = ({ children }: { children: string[] }) => <ol className="list-decimal space-y-2 pl-6">{children.map(text => <li key={text}>{text}</li>)}</ol>;
const Section = ({ title, children }: { title: string; children: ReactNode }) => <section><h2 className="mb-3 text-xl font-bold text-gray-950">{title}</h2>{children}</section>;

export function PolicyPage() {
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  return <LegalLayout title={t('policy.title')}>
    <p className="font-semibold">{t('policy.operator')}</p>
    <Section title={t('policy.corporate.heading')}><Bullets>{[
      t('policy.corporate.item1'),
      t('policy.corporate.item2'),
      t('policy.corporate.item3'),
    ]}</Bullets></Section>
    <Section title={t('policy.complaints.heading')}><Bullets>{[
      t('policy.complaints.item1'),
      t('policy.complaints.item2'),
    ]}</Bullets></Section>
    <p>{t('policy.contactIntro')}</p>
    <p>{t('policy.postalContact')}</p>
    <p>{t('policy.emailLabel')}<a className="text-pink-700 underline" href="mailto:support@queens-live.com">support@queens-live.com</a></p>
  </LegalLayout>;
}

export function TermsPage() {
  const siteUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  return <LegalLayout title={t('terms.title')}>
    <Section title={t('terms.article1.heading')}><p>{t('terms.article1.text1')}</p></Section>
    <Section title={t('terms.article2.heading')}><p>{t('terms.article2.text1')}</p><Numbers>{[
      t('terms.article2.text2'),
      t('terms.article2.text3'),
      t('terms.article2.text4'),
    ]}</Numbers></Section>
    <Section title={t('terms.article3.heading')}><Numbers>{[
      t('terms.article3.text1'),
      t('terms.article3.text2'),
      t('terms.article3.text3'),
      t('terms.article3.text4'),
    ]}</Numbers></Section>
    <Section title={t('terms.article4.heading')}><Numbers>{[
      t('terms.article4.text1'),
      t('terms.article4.text2'),
    ]}</Numbers></Section>
    <Section title={t('terms.article5.heading')}><Numbers>{[
      t('terms.article5.text1'),
      t('terms.article5.text2'),
      t('terms.article5.text3'),
      t('terms.article5.text4'),
    ]}</Numbers></Section>
    <Section title={t('terms.article6.heading')}><p>{t('terms.article6.text1')}</p><Bullets>{[
      t('terms.article6.text2'), t('terms.article6.text3'), t('terms.article6.text4'),
      t('terms.article6.text5'), t('terms.article6.text6'),
      t('terms.article6.text7'), t('terms.article6.text8'),
      t('terms.article6.text9'), t('terms.article6.text10'),
    ]}</Bullets></Section>
    <Section title={t('terms.article7.heading')}><p>{t('terms.article7.text1')}</p><ol className="space-y-2 pl-6">{[
      t('terms.article7.text2'), t('terms.article7.text3'), t('terms.article7.text4'), t('terms.article7.text5'),
      t('terms.article7.text6'), t('terms.article7.text7'), t('terms.article7.text8'),
      t('terms.article7.text9'),
    ].map((text, i) => <li key={text}>({i + 1}) {text}</li>)}</ol><p>{t('terms.article7.text10')}</p></Section>
    <Section title={t('terms.article8.heading')}><Numbers>{[
      t('terms.article8.text1'),
      t('terms.article8.text2'),
      t('terms.article8.text3'),
    ]}</Numbers></Section>
    <Section title={t('terms.article9.heading')}><p>{t('terms.article9.text1', { siteUrl })}</p></Section>
    <Section title={t('terms.article10.heading')}><Numbers>{[t('terms.article10.text1'),t('terms.article10.text2')]}</Numbers></Section>
    <Section title={t('terms.article11.heading')}><p>{t('terms.article11.text1')}</p></Section>
    <Section title={t('terms.article12.heading')}><p>{t('terms.article12.text1')}</p></Section>
    <Section title={t('terms.article13.heading')}><p>{t('terms.article13.text1')}</p></Section>
    <Section title={t('terms.article14.heading')}><Numbers>{[
      t('terms.article14.text1'),
      t('terms.article14.text2'),
    ]}</Numbers></Section>
    <Section title={t('terms.article15.heading')}><p>{t('terms.article15.text1', { siteUrl })}</p></Section>
    <Section title={t('terms.article16.heading')}><p>{t('terms.article16.text1')}</p></Section>
    <Section title={t('terms.article17.heading')}><p>{t('terms.article17.text1')}</p></Section>
    <Section title={t('terms.article18.heading')}><p>{t('terms.article18.text1')}</p></Section>
    <Section title={t('terms.article19.heading')}><p>{t('terms.article19.text1')}</p></Section>
    <p>{t('terms.established')}<br />{t('terms.lastUpdated')}<br />{t('terms.company')}</p>
  </LegalLayout>;
}

export function LawPage() {
  const siteUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  const lawRows = [
    [t('law.seller.label'),t('law.seller.value')], [t('law.responsiblePerson.label'),t('law.responsiblePerson.value')], [t('law.address.label'),t('law.address.value')],
    [t('law.phone.label'),t('law.phone.value')], [t('law.email.label'),'support@queens-live.com'],
    [t('law.website.label'),siteUrl], [t('law.prices.label'),t('law.prices.value')], [t('law.additionalCharges.label'),t('law.additionalCharges.value')],
    [t('law.paymentMethods.label'),t('law.paymentMethods.value')], [t('law.paymentTiming.label'),t('law.paymentTiming.value')], [t('law.deliveryTiming.label'),t('law.deliveryTiming.value')],
    [t('law.returns.label'),t('law.returns.value')],
    [t('law.expiration.label'),t('law.expiration.value')], [t('law.requirements.label'),t('law.requirements.value')],
  ] as const;
  return <LegalLayout title={t('law.title')}><div className="overflow-x-auto"><table className="w-full border-collapse text-left"><tbody>{lawRows.map(([key,value]) => <tr key={key}><th className="w-1/3 border bg-gray-100 p-3 align-top">{key}</th><td className="border p-3">{value}</td></tr>)}</tbody></table></div></LegalLayout>;
}

export function PrivacyPage() {
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  return <LegalLayout title={t('privacy.title')}>
    <p>{t('privacy.intro')}</p>
    <Section title={t('privacy.information.heading')}><p>{t('privacy.information.text1')}<br />{t('privacy.information.text2')}<br />{t('privacy.information.text3')}<br />{t('privacy.information.text4')}<br />{t('privacy.information.text5')}</p></Section>
    <Section title={t('privacy.purposes.heading')}><p>{t('privacy.purposes.text1')}<br />{t('privacy.purposes.text2')}<br />{t('privacy.purposes.text3')}<br />{t('privacy.purposes.text4')}<br />{t('privacy.purposes.text5')}<br />{t('privacy.purposes.text6')}<br />{t('privacy.purposes.text7')}</p></Section>
    <Section title={t('privacy.disclosure.heading')}><p>{t('privacy.disclosure.text1')}<br />{t('privacy.disclosure.text2')}<br />{t('privacy.disclosure.text3')}<br />{t('privacy.disclosure.text4')}</p></Section>
    <Section title={t('privacy.outsourcing.heading')}><p>{t('privacy.outsourcing.text1')}</p></Section>
    <Section title={t('privacy.security.heading')}><p>{t('privacy.security.text1')}</p></Section>
    <Section title={t('privacy.requests.heading')}><p>{t('privacy.requests.text1')}</p></Section>
    <Section title={t('privacy.contact.heading')}><p>{t('privacy.contact.text1')}<br />{t('privacy.contact.text2')}<br />{t('privacy.contact.text3')}</p></Section>
    <p>{t('privacy.established')}<br />{t('privacy.company')}</p>
  </LegalLayout>;
}

export function CompanyPage() {
  const { t } = useTranslation('footer', { keyPrefix: 'legalPages' });
  return <LegalLayout title={t('company.title')}><div className="overflow-x-auto"><table className="w-full border-collapse text-left"><tbody>{[
  [t('company.name.label'),t('company.name.value')], [t('company.representative.label'),t('company.representative.value')], [t('company.address.label'),t('company.address.value')],
  [t('company.business.label'),t('company.business.value')], [t('company.notification.label'),t('company.notification.value')],
].map(([key,value]) => <tr key={key}><th className="w-1/3 border bg-gray-100 p-3">{key}</th><td className="border p-3">{value}</td></tr>)}</tbody></table></div></LegalLayout>;
}
